import { inject, Injectable } from '@angular/core';
import { map, Observable } from 'rxjs';
import type { PieChartConfig } from '../../../shared/echarts/models/chart-types';
import { DASHBOARD_DATA as DASHBOARD_DATA_ROUTES } from '../../../shared/constants/api-routes.constants';
import { OrganizationService } from '../../../core/services/organization.service';
import { HttpClientService } from '../../../core/services/http-client.service';
import { AiVideoReviewService } from './ai-video-review.service';
import { DashboardMockService } from './dashboard-mock.service';
import { DashboardService } from './dashboard.service';
import type { DashboardDataService } from './dashboard-data.token';
import type { MidasAiVideosCardData, TableDataResponse } from './dashboard-mock.service';
import type { MatrixAnalyticsRow } from '../models/matrix-analytics.model';
import type { MatrixInvestigationRow } from '../models/matrix-investigation.model';
import type { TableQuery } from '../../../shared/components/generic-table/models/table-query.model';

@Injectable({ providedIn: 'root' })
export class DashboardDataServiceImpl implements DashboardDataService {
  private readonly mockService = inject(DashboardMockService);
  private readonly dashboardService = inject(DashboardService);
  private readonly aiVideoReviewService = inject(AiVideoReviewService);
  private readonly http = inject(HttpClientService);
  private readonly organizationService = inject(OrganizationService);

  getFilterOptions() {
    // Filters don't have real API yet, use mock
    return this.mockService.getFilterOptions();
  }

  getMatrixAnalytics(query: TableQuery): Observable<TableDataResponse<MatrixAnalyticsRow>> {
    const organizationId = this.organizationService.organizationIdParam();
    const params: Record<string, string | string[]> = { organizationId };
    const daysBack = query.filters?.['daysBack'];
    if (daysBack != null && daysBack !== '') {
      params['daysBack'] = String(daysBack);
    }
    const filterKeys = ['medication', 'clinicalUnit', 'serviceDept'] as const;
    for (const key of filterKeys) {
      const val = query.multiFilters?.[key];
      if (val && val.length > 0) params[key] = val;
    }
    return this.http.get<MatrixAnalyticsRow[] | TableDataResponse<MatrixAnalyticsRow>>(
      DASHBOARD_DATA_ROUTES.MATRIX_ANALYTICS(),
      { params }
    ).pipe(
      map((res) => {
        if (Array.isArray(res)) {
          return { data: res, totalRecords: res.length } satisfies TableDataResponse<MatrixAnalyticsRow>;
        }
        return res;
      })
    );
  }

  getPatientInvestigation(query: TableQuery): Observable<MatrixInvestigationRow[]> {
    const organizationId = this.organizationService.organizationIdParam();
    const params: Record<string, string | string[]> = { organizationId };
    const filterKeys = ['medication', 'clinicalUnit', 'serviceDept'] as const;
    for (const key of filterKeys) {
      const val = query.multiFilters?.[key];
      if (val && val.length > 0) params[key] = val;
    }
    return this.http.get<MatrixInvestigationRow[]>(
      DASHBOARD_DATA_ROUTES.EMPLOYEE_INVESTIGATION(), { params }
    );
  }

  getMatrixTrackerCardData(params: { organizationId: string; daysBack: string }) {
    // Use real API service
    return this.dashboardService.getMatrixTrackerCardData(params);
  }

  getMidasAiVideosCardData(): Observable<MidasAiVideosCardData> {
    // Use real API service and map to expected structure
    const cutoffDate = new Date();
    cutoffDate.setDate(cutoffDate.getDate() - 30);

    return this.aiVideoReviewService.getCounts(cutoffDate).pipe(
      map((dto) => {
        const flagged = dto.flaggedVideosAmount ?? 0;
        const notFlagged = dto.unflaggedVideosAmount ?? 0;

        const chartConfig: PieChartConfig = {
          type: 'pie',
          series: [
            {
              name: 'AI Videos',
              data: [
                {
                  name: 'Not flagged',
                  value: notFlagged,
                  itemStyle: {
                    color: {
                      type: 'linear',
                      x: 0,
                      y: 0,
                      x2: 0,
                      y2: 1,
                      colorStops: [
                        { offset: 0, color: '#fbbf24' },
                        { offset: 1, color: '#f59e0b' },
                      ],
                    },
                  },
                },
                {
                  name: 'Flagged',
                  value: flagged,
                  itemStyle: {
                    color: {
                      type: 'linear',
                      x: 0,
                      y: 0,
                      x2: 0,
                      y2: 1,
                      colorStops: [
                        { offset: 0, color: '#60c5f0' },
                        { offset: 1, color: '#39a8de' },
                      ],
                    },
                  },
                },
              ],
              radius: ['50%', '75%'],
              label: { show: false },
              emphasis: { scale: true },
            },
          ],
          tooltip: { trigger: 'item' },
        };

        return {
          flagged,
          notFlagged,
          periodLabel: 'Last 30 Days',
          chartConfig,
        } satisfies MidasAiVideosCardData;
      })
    );
  }
}
